import React from 'react';
import { PageType, SimulationData } from '../App';

interface ComparisonPageProps {
  setCurrentPage: (page: PageType) => void;
  onStartSimulation: (data?: SimulationData) => void;
}

const ComparisonPage: React.FC<ComparisonPageProps> = ({ setCurrentPage, onStartSimulation }) => {
  return (
    <>
      <section className="uc-hero">
        <div className="uc-hero-inner">
          <span className="uc-hero-label">Multi-Market Comparison</span>
          <h1>You picked a market.<br/><em>We found a better one.</em></h1>
          <p>Submit one target market and we automatically compare it against similar ones — ranked by profitability, speed to revenue, and cost to enter. Sometimes the best market isn't the obvious one.</p>
          <a href="#" onClick={(e) => { e.preventDefault(); onStartSimulation(); }} className="uc-hero-cta">Get Your Free Comparison</a>
        </div>
      </section>

      <div className="uc-section">
        <div className="uc-label"><span>See It In Action</span></div>
        <h2 className="uc-title">You said Germany. We found<br/><em>a better option.</em></h2>
        <p className="uc-sub">Here's what a real comparison looks like. You submit Germany — we automatically rank it against similar markets.</p>
        <div style={{maxWidth:'680px', margin:'0 auto', background:'var(--forest)', borderRadius:'16px', padding:'28px 24px', position:'relative', overflow:'hidden'}}>
          <div style={{position:'absolute', top:0, left:0, right:0, height:'2px', background:'linear-gradient(90deg, var(--sage), var(--gold))', borderRadius:'16px 16px 0 0'}}></div>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px'}}>
            <span style={{fontFamily:'Libre Baskerville,serif', fontSize:'13px', fontWeight:700, color:'var(--white)'}}>Germany vs. Alternatives</span>
          </div>
          <div style={{overflowX:'auto', WebkitOverflowScrolling:'touch' as any, marginRight:'-4px', paddingRight:'4px'}}>
            <table className="mmc-table" style={{minWidth:'480px'}}>
              <thead>
                <tr><th>#</th><th>Market</th><th>Score</th><th>Profitable</th><th>12-Mo Rev</th><th>Verdict</th><th></th></tr>
              </thead>
              <tbody>
                <tr className="mmc-winner">
                  <td>1</td>
                  <td>🇳🇱 Netherlands <span className="mmc-tag go" style={{marginLeft:'4px', fontSize:'7px', verticalAlign:'middle'}}>Recommended</span></td>
                  <td><div className="mmc-score"><span>8.4</span><div className="mmc-score-bar"><div className="mmc-score-fill high" style={{width:'84%'}}></div></div></div></td>
                  <td>Month 7</td><td>€412K</td>
                  <td><span className="mmc-tag go">Strong GO</span></td>
                  <td><a href="#" className="mmc-insights-link" onClick={(e) => { e.preventDefault(); onStartSimulation({ country: 'Netherlands' }); }}>View Insights</a></td>
                </tr>
                <tr>
                  <td>2</td><td>🇩🇰 Denmark</td>
                  <td><div className="mmc-score"><span>7.8</span><div className="mmc-score-bar"><div className="mmc-score-fill high" style={{width:'78%'}}></div></div></div></td>
                  <td>Month 8</td><td>€298K</td>
                  <td><span className="mmc-tag go">GO</span></td>
                  <td><a href="#" className="mmc-insights-link" onClick={(e) => { e.preventDefault(); onStartSimulation({ country: 'Denmark' }); }}>View Insights</a></td>
                </tr>
                <tr>
                  <td>3</td><td>🇩🇪 Germany</td>
                  <td><div className="mmc-score"><span>7.0</span><div className="mmc-score-bar"><div className="mmc-score-fill mid" style={{width:'70%'}}></div></div></div></td>
                  <td>Month 10</td><td>€353K</td>
                  <td><span className="mmc-tag caution">Conditional</span></td>
                  <td><a href="#" className="mmc-insights-link" onClick={(e) => { e.preventDefault(); onStartSimulation({ country: 'Germany' }); }}>View Insights</a></td>
                </tr>
                <tr>
                  <td>4</td><td>🇵🇱 Poland</td>
                  <td><div className="mmc-score"><span>6.5</span><div className="mmc-score-bar"><div className="mmc-score-fill mid" style={{width:'65%'}}></div></div></div></td>
                  <td>Month 11</td><td>€215K</td>
                  <td><span className="mmc-tag caution">Conditional</span></td>
                  <td><a href="#" className="mmc-insights-link" onClick={(e) => { e.preventDefault(); onStartSimulation({ country: 'Poland' }); }}>View Insights</a></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <p style={{textAlign:'center', fontSize:'13px', color:'var(--text-light)', marginTop:'20px', fontStyle:'italic'}}>Netherlands scores 20% higher than Germany — with faster profitability and higher projected revenue.</p>
      </div>

      <div className="uc-section" style={{paddingTop:0}}>
        <div className="uc-label"><span>What You Get</span></div>
        <h2 className="uc-title">Every comparison includes<br/><em>four dimensions.</em></h2>
        <p className="uc-sub">Not just a ranking — a complete analysis of why one market outperforms another.</p>
        <div className="uc-features">
          <div className="uc-feature">
            <div className="uc-feature-icon"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg></div>
            <h3>Automatic alternative discovery</h3>
            <p>Pick your target country. Our system instantly identifies 4-5 comparable markets you may not have considered, based on market size, growth rate, regulatory environment, and competitive density.</p>
          </div>
          <div className="uc-feature">
            <div className="uc-feature-icon"><svg viewBox="0 0 24 24"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg></div>
            <h3>Side-by-side financial simulation</h3>
            <p>Each market gets its own 12-month projection covering revenue, costs, break-even timeline, and cash flow. All in one view so you can compare apples to apples.</p>
          </div>
          <div className="uc-feature">
            <div className="uc-feature-icon"><svg viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></div>
            <h3>AI-powered entry scoring</h3>
            <p>Every market receives a composite Entry Score (1-10) combining profitability potential, competitive difficulty, regulatory complexity, and timing. One number to compare opportunities.</p>
          </div>
          <div className="uc-feature">
            <div className="uc-feature-icon"><svg viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></div>
            <h3>Clear recommendation</h3>
            <p>Don't just get data. Get a verdict. Our simulation tells you which market ranks highest, why, and whether your original target is worth pursuing or if you should pivot.</p>
          </div>
        </div>
      </div>

      <div className="uc-section-dark">
        <div className="uc-inner">
          <div className="uc-label"><span>How It Works</span></div>
          <h2 className="uc-title">One target in.<br/><em>Five markets out.</em></h2>
          <p className="uc-sub">The entire comparison is generated automatically from a single input.</p>
          <div className="uc-scenario">
            <div className="uc-scenario-card" style={{background:'rgba(255,255,255,0.04)', borderColor:'rgba(255,255,255,0.06)'}}>
              <div className="uc-scenario-num">Step 01</div>
              <h4 style={{color:'var(--white)'}}>You pick a country</h4>
              <p style={{color:'rgba(255,255,255,0.5)'}}>Submit your target market — Germany, Brazil, Japan, anywhere. Takes 2 minutes.</p>
            </div>
            <div className="uc-scenario-card" style={{background:'rgba(255,255,255,0.04)', borderColor:'rgba(255,255,255,0.06)'}}>
              <div className="uc-scenario-num">Step 02</div>
              <h4 style={{color:'var(--white)'}}>We find alternatives</h4>
              <p style={{color:'rgba(255,255,255,0.5)'}}>Our system identifies 4-5 similar markets and runs a full financial simulation for each one.</p>
            </div>
            <div className="uc-scenario-card" style={{background:'rgba(255,255,255,0.04)', borderColor:'rgba(255,255,255,0.06)'}}>
              <div className="uc-scenario-num">Step 03</div>
              <h4 style={{color:'var(--white)'}}>You see the ranking</h4>
              <p style={{color:'rgba(255,255,255,0.5)'}}>Receive a ranked comparison with Entry Scores, profitability timelines, and a clear recommendation on where to start.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ComparisonPage;
